import fEvents from '../../js/events';
import { attachEvent } from '../../js/tools';

export const MODAL_TYPE = {
    CONFIRM: 'CONFIRM',
    ALERT: 'ALERT'
};

export interface IConfirmEvent extends Event {
    optionalData: any;
}

export default class Modalbox extends HTMLElement {
    private isConfirmed: boolean;
    constructor() {
        super();

        const shadowRoot = this.attachShadow({ mode: 'open' });
        shadowRoot.innerHTML = this.render();

        this.isConfirmed = null;

        attachEvent('.modal-cancel', 'click', () => this.decline(), this.shadowRoot);
        attachEvent('.close-btn i', 'click', () => this.decline(), this.shadowRoot);
        attachEvent('.modal-background', 'click', () => this.decline(), this.shadowRoot);
        attachEvent('.modal-confirm', 'click', () => this.accept(), this.shadowRoot);
    }

    show(question?: string, type = MODAL_TYPE.CONFIRM): void {

        const modalContainer: HTMLElement = this.shadowRoot.querySelector('.modal-container');
        const modalBody: HTMLElement = modalContainer.querySelector('slot');
        const cancelBtn: HTMLButtonElement = modalContainer.querySelector('.modal-cancel');
        const confirmBtn: HTMLButtonElement = modalContainer.querySelector('.modal-confirm');
        const buttonsContainer: HTMLElement = modalContainer.querySelector('.buttons-container');

        if (type === MODAL_TYPE.ALERT) {
            confirmBtn.style.display = 'none';
            cancelBtn.innerText = 'Ok';
        } else {
            confirmBtn.style.display = 'block';
            cancelBtn.innerText = 'Cancel';
        }

        modalBody.innerHTML = `<div slot="content" class="modal-body">${question}</div>`;
        buttonsContainer.style.display = question ? 'flex' : 'none';
        modalContainer.style.display = 'flex';
    }

    confirm(question: string): Promise<any> {
        this.isConfirmed = null;
        const modalContainer: HTMLElement = this.shadowRoot.querySelector('.modal-container');
        const modalBody: HTMLElement = modalContainer.querySelector('slot');
        const cancelBtn: HTMLButtonElement = modalContainer.querySelector('.modal-cancel');
        const confirmBtn: HTMLButtonElement = modalContainer.querySelector('.modal-confirm');
        const buttonsContainer: HTMLElement = modalContainer.querySelector('.buttons-container');

        confirmBtn.style.display = 'block';
        cancelBtn.innerText = 'Cancel';

        modalBody.innerHTML = `<div slot="content" class="modal-body">${question}</div>`;
        buttonsContainer.style.display = question ? 'flex' : 'none';
        modalContainer.style.display = 'flex';

        const self = this;
        return new Promise(resolve => {
            const waitForConfirmation = () => {
                if (self.isConfirmed === null) {//we want it to match
                    setTimeout(waitForConfirmation, 50);//wait 50 millisecnds then recheck
                    return;
                }
                resolve(self.isConfirmed);
            };
            waitForConfirmation();
        });
    }

    static get observedAttributes(): string[] {
        return ['width', 'height', 'top'];
    }

    attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        if (attrName === 'width') {
            const container: HTMLElement = this.shadowRoot.querySelector('.modal-content');
            container.style.width = newVal;
        }

        if (attrName === 'height') {
            const container: HTMLElement = this.shadowRoot.querySelector('.modal-content');
            container.style.height = newVal;
        }

        if (attrName === 'top') {
            const container: HTMLElement = this.shadowRoot.querySelector('.modal-container');
            container.style.top = newVal;
        }
    }

    hide(): void {
        const modalContainer: HTMLElement = this.shadowRoot.querySelector('.modal-container');
        this.removeAttribute('confirm-name');
        modalContainer.style.display = 'none';
    }

    decline(): void {
        this.isConfirmed = false;
        this.hide();
        fEvents.dispatch(this, 'modal-close', {});
    }

    accept(): void {
        this.isConfirmed = true;

        // Todo: don't dispatch event after promis confirmation works.
        const confirmEventName = this.getAttribute('confirm-name');
        const optionalData = this.getAttribute('data-optional');
        const confirmEvent = new Event(confirmEventName, {
            bubbles: true,
            composed: true
        });

        this.hide();
        (confirmEvent as IConfirmEvent).optionalData = optionalData;
        this.dispatchEvent(confirmEvent);
    }

    render = (): string => `
        <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons">
        <link rel="stylesheet" href="/styles/index.css">
        <link rel="stylesheet" href="/styles/styles.css">
        <style>
            .modal-container {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                display: none;
                align-items: center;
                justify-content: center;
                z-index: 2;
            }

            .modal-background {
                position: fixed;
                background-color: rgb(0, 0, 0, 0.6);
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                z-index: 2;
            }

            .modal-content {
                background-color: #fff;
                z-index: 3;
                padding: 24px 32px;
                border-radius: 4px;
                max-height: 90%;
                overflow: auto;
            }

            .modal-body {
                text-align: center;
                font-size: 16px;
                line-height: 1.5em;
                margin: 20px 0;
            }

            .close-btn {
                text-align: right;
                background: transparent;
                border: none;
            }

            .close-btn i {
                cursor: pointer;
            }
            .close-btn-container {
                display: flex;
                justify-content: flex-end;
                margin-bottom: 12px;
            }

            .buttons-container {
                display: none;
                justify-content: space-around;
            }
        </style>
        <div class="modal-container">
            <div class="modal-background"></div>
            <div class="modal-content">
                <div class="close-btn-container">
                    <button class="close-btn"><i class="material-icons">close</i></button>
                </div>
                <slot name="content"></slot>
                <div class="buttons-container">
                    <button class="fl-button fl-button-tertiary modal-cancel">Cancel</button>
                    <button class="fl-button mdl-button--primary modal-confirm">Yes</button>
                </div>
            </div>
        </div>
    `;
}

customElements.define('modal-box', Modalbox);
